import type { Dispatch } from 'redux';
import { type ActionsObject, makeAction, makeReducer } from '@finn-no/redux-actions';
import getConfig from 'next/config';
import { debounce } from 'ts-debounce';

import { ApiActionStatus, type DirectHotelMap, type DirectHotelPrices, type Filters, type ThunkResult } from '@/interfaces';
import { safeFormatISODate } from 'shared/util/datetime-fns';
import { roomsToQuery } from '@/utils/url/queryParamConverters';

const DIRECT_PRICE_REQUEST_DELAY = 300;

const {
    publicRuntimeConfig: { basePath },
} = getConfig();

export const makeDirectPriceApiRequest = (hotelIds: number[], isKayakId: boolean, filters: Filters, dispatch: Dispatch): void => {
    const checkIn = safeFormatISODate(filters.checkInDate);
    const checkOut = safeFormatISODate(filters.checkOutDate);
    const rooms = roomsToQuery(filters.rooms).join('&');

    const url = `${basePath}/api/prices/?hotelIds=${hotelIds.join(',')}&checkInDate=${checkIn}&checkOutDate=${checkOut}&${rooms}${isKayakId ? '&isKayakIds=true' : ''}`;
    fetch(url).then(async (response) => {
        const text = await response.text();
        try {
            const data = JSON.parse(text) as DirectHotelMap;
            const hotels = Object.keys(data);
            const incompleteHotels = hotels
                .filter((hotelId) => !data[hotelId].reduce((out, offer) => out && offer.isComplete, true))
                .map((hotelId) => Number.parseInt(hotelId, 10));
            if (incompleteHotels.length > 0) {
                setTimeout(() => {
                    debouncedFetchDirectPrices(incompleteHotels, isKayakId, filters, dispatch);
                }, DIRECT_PRICE_REQUEST_DELAY);
                dispatch(actions.setPartialDirectPrices(data));
            } else {
                dispatch(actions.setDirectPrices(data));
            }
        } catch (e) {
            dispatch(actions.setDirectPriceError());
        }
    });
};

export const debouncedFetchDirectPrices = debounce(makeDirectPriceApiRequest, DIRECT_PRICE_REQUEST_DELAY);

export const fetchDirectPrices =
    (hotelId: number | number[], isKayakId: boolean): ThunkResult<Promise<void>> =>
    async (dispatch, getState): Promise<void> => {
        const hotelIdArray = Array.isArray(hotelId) ? hotelId : [hotelId];
        dispatch(actions.setDirectPricesPending(hotelIdArray));
        debouncedFetchDirectPrices(hotelIdArray, isKayakId, getState().filters, dispatch);
    };

/*
 * Action creators
 */
export const actions: ActionsObject<DirectHotelPrices> = {
    setDirectPricesPending: makeAction(
        'DIRECT/PENDING',
        (state): DirectHotelPrices => ({
            ...state,
            status: ApiActionStatus.PENDING,
        }),
    ),
    setPartialDirectPrices: makeAction(
        'DIRECT/PARTIAL',
        (state, action): DirectHotelPrices => ({
            ...state,
            status: ApiActionStatus.PENDING,
            hotels: { ...state.hotels, ...action.payload },
        }),
    ),
    setDirectPrices: makeAction(
        'DIRECT/OK',
        (state, action): DirectHotelPrices => ({
            ...state,
            status: action.payload.status || ApiActionStatus.OK,
            hotels: { ...state.hotels, ...action.payload },
        }),
    ),
    setDirectPriceError: makeAction('DIRECT/ERROR', (state): DirectHotelPrices => ({ ...state, status: ApiActionStatus.ERROR })),
};

export const directPricesReducer = makeReducer<DirectHotelPrices>(actions);
